// App menu switches for Super Admin ▸ Mobile Apps (spec §5, P4–P6 plan R1/R2).
// Names and order always come from the app's own menu; only the iPhone/Android
// switches are ours. New menu items start OFF (spec D6). Purchase surfaces are
// locked OFF (Apple 3.1.1) whatever was saved.
import { MOBILE_PURCHASE_ROUTES } from '@platform/shared-types';
import type { MobileAppId, MobileAppMenu, MobileAppMenuEntry, MobilePlatform } from '@platform/shared-types';

// Runtime shared-types import, guarded (a stale Vite pre-bundle resolves new
// exports to undefined). If missing, callers must disable Save — see lockTableReady.
const LOCKED: Record<string, readonly string[]> | undefined = MOBILE_PURCHASE_ROUTES as any;
if (!LOCKED) console.error('[mobile-apps] MOBILE_PURCHASE_ROUTES missing from @platform/shared-types — RESTART the dev server (stale Vite pre-bundle).');
export const lockTableReady = !!LOCKED;

export interface AppMenuRow extends MobileAppMenuEntry { locked: boolean }

export function isLockedRoute(app: MobileAppId, route: string | undefined): boolean {
  if (!route) return false;
  return (LOCKED?.[app] ?? []).some((p) => route === p || route.startsWith(`${p}/`));
}

const savedMap = (saved: Pick<MobileAppMenu, 'entries'> | null) => new Map((saved?.entries ?? []).map((e) => [e.id, e]));

function row(app: MobileAppId, id: string, label: string, group: string, route: string | undefined, saved: Map<string, MobileAppMenuEntry>): AppMenuRow {
  const s = saved.get(id);
  const locked = isLockedRoute(app, route);
  return { id, label, group, route, ios: !locked && !!s?.ios, android: !locked && !!s?.android, locked };
}

/** RubyTill: from the /menus sidebar config (groups → items, subgroups → items). */
export function rubyTillEntries(sidebar: { groups?: any[] } | null, saved: Pick<MobileAppMenu, 'entries'> | null): AppMenuRow[] {
  const m = savedMap(saved);
  const out: AppMenuRow[] = [];
  for (const g of sidebar?.groups ?? []) {
    for (const it of g.items ?? []) if (it?.id && it?.route) out.push(row('rubytill', it.id, it.label, g.label, it.route, m));
    for (const sg of g.subgroups ?? []) {
      for (const it of sg.items ?? []) if (it?.id && it?.route) out.push(row('rubytill', it.id, it.label, `${g.label} › ${sg.label}`, it.route, m));
    }
  }
  return out;
}

/** RubyTill Admin: from the exported super-admin nav (id = path). */
export function adminEntries(groups: { title: string; items: { path: string; label: string }[] }[], saved: Pick<MobileAppMenu, 'entries'> | null): AppMenuRow[] {
  const m = savedMap(saved);
  return groups.flatMap((g) => g.items.map((it) => row('rubytill-admin', it.path, it.label, g.title, it.path, m)));
}

export const countOn = (rows: AppMenuRow[], p: MobilePlatform) => rows.filter((r) => r[p]).length;

/** What gets saved: every row (switch state), locked rows forced off. */
export const toEntries = (rows: AppMenuRow[]): MobileAppMenuEntry[] =>
  rows.map(({ locked, ...e }) => ({ ...e, ios: !locked && e.ios, android: !locked && e.android }));
